import { useState, useEffect, useRef } from 'react';
import { MemoryState } from './index';
import styles from '../../styles/MemoryDisplay.module.css';
import { 
  listPresets, 
  loadMemoryState, 
  exportMemoryState, 
  importMemoryState,
  shareMemoryState,
  loadSharedMemoryState
} from './storage';

interface MemoryDisplayProps {
  memory?: MemoryState;
  userId?: string;
  onUserChange?: (userId: string) => void;
}

export function MemoryDisplay({ memory, userId, onUserChange }: MemoryDisplayProps) {
  const [availableUsers, setAvailableUsers] = useState<string[]>([]);
  const [selectedUser, setSelectedUser] = useState<string>(userId || '');
  const [currentMemory, setCurrentMemory] = useState<MemoryState | null>(null);
  const [isSharing, setIsSharing] = useState(false);
  const [sharedState, setSharedState] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Cargar lista de usuarios disponibles
  useEffect(() => {
    async function loadUsers() {
      try {
        const users = await listPresets();
        setAvailableUsers(users);
        if (!selectedUser && users.length > 0) {
          handleUserChange(users[0]);
        }
      } catch (error) {
        console.error('Error loading users:', error);
      }
    }
    loadUsers();
  }, []);

  // Cargar memoria cuando cambia el usuario seleccionado
  useEffect(() => {
    async function loadMemory() {
      if (selectedUser) {
        try {
          const state = await loadMemoryState(selectedUser);
          setCurrentMemory(state);
        } catch (error) {
          console.error('Error loading memory:', error);
        }
      }
    }
    loadMemory();
  }, [selectedUser]);

  // Escuchar eventos de actualización de memoria
  useEffect(() => {
    const handleMemoryUpdate = (event: CustomEvent<{ userId: string; state: MemoryState }>) => {
      const { userId: updatedUserId, state } = event.detail;
      if (updatedUserId === selectedUser) {
        setCurrentMemory(state);
      }
    };

    window.addEventListener('memory_updated', handleMemoryUpdate as EventListener);
    return () => {
      window.removeEventListener('memory_updated', handleMemoryUpdate as EventListener);
    };
  }, [selectedUser]);

  // Manejar cambio de usuario
  const handleUserChange = (newUserId: string) => {
    setSelectedUser(newUserId);
    onUserChange?.(newUserId);
  };

  // Manejar exportación de estado
  const handleExport = () => {
    if (!selectedUser) return;
    try {
      exportMemoryState(selectedUser);
    } catch (error) {
      console.error('Error exporting state:', error);
    }
  };

  // Manejar importación de estado
  const handleImport = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !selectedUser) return;

    try {
      const newState = await importMemoryState(selectedUser, file);
      setCurrentMemory(newState);
    } catch (error) {
      console.error('Error importing state:', error);
    }

    // Limpiar input para permitir cargar el mismo archivo de nuevo
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Manejar compartir estado
  const handleShare = () => {
    if (!selectedUser) return;
    try {
      const state = shareMemoryState(selectedUser);
      setSharedState(state);
      setIsSharing(true);
    } catch (error) {
      console.error('Error sharing state:', error);
    }
  };

  // Manejar cargar estado compartido
  const handleLoadShared = () => {
    if (!selectedUser || !sharedState) return;
    try {
      const newState = loadSharedMemoryState(selectedUser, sharedState);
      setCurrentMemory(newState);
      setIsSharing(false);
      setSharedState('');
    } catch (error) {
      console.error('Error loading shared state:', error);
    }
  };

  // Mostrar el estado de memoria proporcionado o el estado cargado
  const displayMemory = memory || currentMemory;

  return (
    <div className={styles["memory-display"]}>
      <div className={styles["memory-header"]}>
        <select 
          value={selectedUser}
          onChange={(e) => handleUserChange(e.target.value)}
          className={styles["memory-select"]}
        >
          <option value="">Select User</option>
          {availableUsers.map(user => (
            <option key={user} value={user}>
              {user}
            </option>
          ))}
        </select>
      </div>

      {selectedUser && (
        <div className={styles["memory-actions"]}>
          <button
            onClick={handleExport}
            className={styles["memory-action-btn"]}
          >
            💾 Export
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json"
            onChange={handleImport}
            className={styles.hidden}
            id="import-memory"
          />
          <label
            htmlFor="import-memory"
            className={styles["memory-action-btn"]}
          >
            📂 Import
          </label>
          <button
            onClick={handleShare}
            className={styles["memory-action-btn"]}
          >
            🔗 Share
          </button>
        </div>
      )}
      
      {isSharing && (
        <div style={{ marginBottom: '1rem' }}>
          <textarea
            value={sharedState}
            onChange={(e) => setSharedState(e.target.value)}
            className={styles["shared-state-textarea"]}
            rows={3}
            placeholder="Paste shared state here..."
          />
          <button
            onClick={handleLoadShared}
            className={styles["memory-action-btn"]}
          >
            📥 Load Shared State
          </button>
        </div>
      )}
      
      {displayMemory ? (
        <div className={styles["memory-content"]}>
          <pre>
            {JSON.stringify(displayMemory, null, 2)}
          </pre>
        </div>
      ) : (
        <div className={styles["memory-content"]}>
          <span style={{ color: 'rgba(156, 163, 175, 0.8)' }}>
            Select a user to view memory state
          </span>
        </div>
      )}
    </div>
  );
}
